import React, { useState, useMemo } from 'react';
import { PickupGame, SportType, AthleteProfile } from '../types';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  MapPin, 
  Users, 
  Plus, 
  Share2, 
  Check, 
  Flame, 
  Filter, 
  Sparkles,
  Trophy,
  ArrowRight
} from 'lucide-react';
import { getSportLabel } from '../utils/sportUtils';

interface GameCalendarViewProps {
  games: PickupGame[];
  user: AthleteProfile;
  onOpenHostGame?: (preselectedDate?: string) => void;
  onJoinGame?: (gameId: string) => void;
  onShareGame?: (game: PickupGame) => void;
}

type CalendarMode = 'month' | 'week';

export const GameCalendarView: React.FC<GameCalendarViewProps> = ({
  games,
  user,
  onOpenHostGame,
  onJoinGame,
  onShareGame,
}) => {
  const [viewMode, setViewMode] = useState<CalendarMode>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date('2026-08-02'));
  const [selectedSport, setSelectedSport] = useState<SportType | 'all'>('all');
  const [selectedDayGamesDate, setSelectedDayGamesDate] = useState<string | null>('2026-08-02');
  const [selectedGameDetail, setSelectedGameDetail] = useState<PickupGame | null>(null);

  // Filter games based on sport
  const filteredGames = useMemo(() => {
    if (selectedSport === 'all') return games;
    return games.filter((g) => g.sport === selectedSport);
  }, [games, selectedSport]);

  // Map games by YYYY-MM-DD key for rapid calendar lookup
  const gamesByDate = useMemo(() => {
    const map: Record<string, PickupGame[]> = {};
    filteredGames.forEach((game) => {
      // dateTime might be ISO like "2026-08-02T18:00:00" or simple date format
      let dateKey = game.dateTime.split('T')[0];
      if (!dateKey || dateKey.length < 10) {
        // Fallback try parsing or default to today
        const parsed = new Date(game.dateTime);
        if (!isNaN(parsed.getTime())) {
          dateKey = parsed.toISOString().split('T')[0];
        } else {
          dateKey = '2026-08-02';
        }
      }
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(game);
    });
    return map;
  }, [filteredGames]);

  // Calendar Helpers for Month View
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Navigation handlers
  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(year, month - 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(year, month + 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date('2026-08-02'));
    setSelectedDayGamesDate('2026-08-02');
  };

  // Generate Month Grid Days
  const monthGridDays = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sun
    const totalDays = lastDayOfMonth.getDate();

    const days: Array<{
      dateString: string;
      dayNumber: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      dateObj: Date;
    }> = [];

    // Prev month pad
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const pDay = prevMonthLastDay - i;
      const pDate = new Date(year, month - 1, pDay);
      const dateString = pDate.toISOString().split('T')[0];
      days.push({
        dateString,
        dayNumber: pDay,
        isCurrentMonth: false,
        isToday: dateString === '2026-08-02',
        dateObj: pDate,
      });
    }

    // Current month days
    for (let d = 1; d <= totalDays; d++) {
      const cDate = new Date(year, month, d);
      // Format YYYY-MM-DD carefully without timezone shift issues
      const yStr = cDate.getFullYear();
      const mStr = String(cDate.getMonth() + 1).padStart(2, '0');
      const dStr = String(d).padStart(2, '0');
      const dateString = `${yStr}-${mStr}-${dStr}`;

      days.push({
        dateString,
        dayNumber: d,
        isCurrentMonth: true,
        isToday: dateString === '2026-08-02',
        dateObj: cDate,
      });
    }

    // Next month pad to complete 35 or 42 grid cells
    const remaining = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const nDate = new Date(year, month + 1, i);
      const yStr = nDate.getFullYear();
      const mStr = String(nDate.getMonth() + 1).padStart(2, '0');
      const dStr = String(i).padStart(2, '0');
      const dateString = `${yStr}-${mStr}-${dStr}`;

      days.push({
        dateString,
        dayNumber: i,
        isCurrentMonth: false,
        isToday: dateString === '2026-08-02',
        dateObj: nDate,
      });
    }

    return days;
  }, [year, month]);

  // Generate Week View Days
  const weekDays = useMemo(() => {
    const days: Array<{
      dateString: string;
      dayName: string;
      dayNumber: number;
      isToday: boolean;
      dateObj: Date;
    }> = [];

    // Find Sunday of the selected week
    const currentDayOfWeek = currentDate.getDay();
    const sundayDate = new Date(currentDate);
    sundayDate.setDate(currentDate.getDate() - currentDayOfWeek);

    for (let i = 0; i < 7; i++) {
      const wDate = new Date(sundayDate);
      wDate.setDate(sundayDate.getDate() + i);

      const yStr = wDate.getFullYear();
      const mStr = String(wDate.getMonth() + 1).padStart(2, '0');
      const dStr = String(wDate.getDate()).padStart(2, '0');
      const dateString = `${yStr}-${mStr}-${dStr}`;

      const dayName = wDate.toLocaleDateString('default', { weekday: 'short' });

      days.push({
        dateString,
        dayName,
        dayNumber: wDate.getDate(),
        isToday: dateString === '2026-08-02',
        dateObj: wDate,
      });
    }

    return days;
  }, [currentDate]);

  // Week View Header Date Range String
  const weekRangeString = useMemo(() => {
    if (weekDays.length < 7) return '';
    const start = weekDays[0].dateObj.toLocaleDateString('default', { month: 'short', day: 'numeric' });
    const end = weekDays[6].dateObj.toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} – ${end}`;
  }, [weekDays]);

  // Games for currently focused day
  const focusedDayGames = useMemo(() => {
    if (!selectedDayGamesDate) return [];
    return gamesByDate[selectedDayGamesDate] || [];
  }, [selectedDayGamesDate, gamesByDate]);

  // Format date readable
  const formatReadableDate = (dateStr: string) => {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return d.toLocaleDateString('default', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
    }
    return dateStr;
  };

  return (
    <div className="space-y-6 text-white animate-fadeIn">
      {/* Calendar Control Top Header */}
      <div className="bg-indigo-900/60 p-5 rounded-[2.5rem] border border-white/10 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        
        {/* Title & View Switcher */}
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
            <CalendarIcon className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div>
            <h2 className="text-xl font-black italic uppercase tracking-tight text-white flex items-center">
              <span>Pickup Games Calendar</span>
              <span className="ml-2.5 px-2.5 py-0.5 bg-lime-400/20 border border-lime-400/40 text-lime-400 text-[10px] font-black uppercase rounded-full">
                Live Sync
              </span>
            </h2>
            <p className="text-xs text-indigo-200/70 font-semibold">
              {viewMode === 'month' ? monthName : weekRangeString}
            </p>
          </div>
        </div>

        {/* Action Controls & Toggles */}
        <div className="flex flex-wrap items-center gap-2">
          
          {/* Sport Filter Pills */}
          <div className="flex items-center space-x-1 bg-indigo-950 p-1 rounded-2xl border border-white/10 text-xs">
            <button
              onClick={() => setSelectedSport('all')}
              className={`px-3 py-1.5 rounded-xl font-black text-[11px] uppercase transition ${
                selectedSport === 'all'
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              All Sports
            </button>
            <button
              onClick={() => setSelectedSport(user.primarySport)}
              className={`px-3 py-1.5 rounded-xl font-black text-[11px] uppercase transition ${
                selectedSport === user.primarySport
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              {getSportLabel(user.primarySport)}
            </button>
          </div>

          {/* View Toggle (Monthly vs Weekly) */}
          <div className="flex items-center bg-indigo-950 p-1 rounded-2xl border border-white/10 text-xs font-black uppercase">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 rounded-xl transition ${
                viewMode === 'month'
                  ? 'bg-indigo-800 text-white border border-white/20 shadow-md'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 rounded-xl transition ${
                viewMode === 'week'
                  ? 'bg-indigo-800 text-white border border-white/20 shadow-md'
                  : 'text-indigo-300 hover:text-white'
              }`}
            >
              Week
            </button>
          </div>

          {/* Date Nav Buttons */}
          <div className="flex items-center space-x-1 bg-indigo-950 p-1 rounded-2xl border border-white/10">
            <button
              onClick={handlePrev}
              className="p-1.5 hover:bg-indigo-800 rounded-xl transition text-indigo-200"
              title="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleToday}
              className="px-2.5 py-1 text-[11px] font-black uppercase text-lime-400 hover:bg-indigo-800 rounded-xl transition"
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className="p-1.5 hover:bg-indigo-800 rounded-xl transition text-indigo-200"
              title="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Host Game Button */}
          {onOpenHostGame && (
            <button
              onClick={() => onOpenHostGame(selectedDayGamesDate || undefined)}
              className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic rounded-2xl text-xs uppercase tracking-wider flex items-center space-x-1.5 shadow-md shadow-lime-400/20"
            >
              <Plus className="w-4 h-4 stroke-[3]" />
              <span>Host Game</span>
            </button>
          )}
        </div>
      </div>

      {/* MONTH VIEW GRID */}
      {viewMode === 'month' && (
        <div className="bg-indigo-950/80 rounded-[2.5rem] p-4 sm:p-6 border border-white/10 shadow-2xl space-y-4">
          
          {/* Day of Week Headers */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center text-xs font-black uppercase tracking-wider text-indigo-300/80 pb-2 border-b border-white/10">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Month Calendar Grid */}
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2.5">
            {monthGridDays.map((cell) => {
              const dayGames = gamesByDate[cell.dateString] || [];
              const isSelected = selectedDayGamesDate === cell.dateString;

              return (
                <div
                  key={cell.dateString}
                  onClick={() => setSelectedDayGamesDate(cell.dateString)}
                  className={`min-h-[90px] sm:min-h-[110px] p-2 rounded-2xl border transition-all duration-200 flex flex-col justify-between cursor-pointer group relative overflow-hidden ${
                    isSelected
                      ? 'bg-indigo-900 border-lime-400 ring-2 ring-lime-400/30 shadow-lg'
                      : cell.isToday
                      ? 'bg-indigo-900/60 border-lime-400/60'
                      : cell.isCurrentMonth
                      ? 'bg-indigo-900/30 border-white/10 hover:bg-indigo-900/50 hover:border-white/20'
                      : 'bg-indigo-950/40 border-white/5 opacity-40 hover:opacity-70'
                  }`}
                >
                  {/* Top Bar: Date Number + Today Badge */}
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs sm:text-sm font-black font-mono px-2 py-0.5 rounded-lg ${
                        cell.isToday
                          ? 'bg-lime-400 text-black font-extrabold'
                          : isSelected
                          ? 'text-lime-300 font-bold'
                          : cell.isCurrentMonth
                          ? 'text-white'
                          : 'text-indigo-300'
                      }`}
                    >
                      {cell.dayNumber}
                    </span>

                    {dayGames.length > 0 && (
                      <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30">
                        {dayGames.length} {dayGames.length === 1 ? 'game' : 'games'}
                      </span>
                    )}
                  </div>

                  {/* Scheduled Games Pills in Cell */}
                  <div className="mt-1.5 space-y-1 flex-1 overflow-y-auto max-h-[60px] custom-scrollbar">
                    {dayGames.slice(0, 2).map((g) => (
                      <div
                        key={g.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGameDetail(g);
                        }}
                        className={`text-[10px] p-1.5 rounded-xl border font-semibold truncate flex items-center justify-between transition hover:scale-102 ${
                          g.isHostedByMe
                            ? 'bg-lime-400/20 border-lime-400/40 text-lime-300'
                            : 'bg-indigo-950/80 border-white/10 text-indigo-100 hover:border-lime-400/40'
                        }`}
                      >
                        <span className="truncate font-bold italic">
                          {g.title}
                        </span>
                        <span className="text-[9px] font-mono opacity-80 shrink-0 ml-1">
                          {g.currentPlayers}/{g.maxPlayers}
                        </span>
                      </div>
                    ))}

                    {dayGames.length > 2 && (
                      <p className="text-[9px] font-bold text-lime-400/90 text-center italic">
                        +{dayGames.length - 2} more
                      </p>
                    )}
                  </div>

                  {/* Empty Cell Quick Host Hint */}
                  {dayGames.length === 0 && cell.isCurrentMonth && (
                    <div className="hidden group-hover:flex items-center justify-center pt-1 text-[9px] font-bold text-indigo-300/60 italic">
                      + Click to view
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* WEEK VIEW TIMELINE */}
      {viewMode === 'week' && (
        <div className="bg-indigo-950/80 rounded-[2.5rem] p-4 sm:p-6 border border-white/10 shadow-2xl space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            {weekDays.map((wDay) => {
              const dayGames = gamesByDate[wDay.dateString] || [];
              const isSelected = selectedDayGamesDate === wDay.dateString;

              return (
                <div
                  key={wDay.dateString}
                  onClick={() => setSelectedDayGamesDate(wDay.dateString)}
                  className={`p-3.5 rounded-3xl border transition-all duration-200 flex flex-col justify-between space-y-3 cursor-pointer ${
                    isSelected
                      ? 'bg-indigo-900 border-lime-400 ring-2 ring-lime-400/30'
                      : wDay.isToday
                      ? 'bg-indigo-900/60 border-lime-400/50'
                      : 'bg-indigo-900/30 border-white/10 hover:bg-indigo-900/50'
                  }`}
                >
                  {/* Week Day Header */}
                  <div className="flex items-center justify-between border-b border-white/10 pb-2">
                    <div>
                      <span className="text-[10px] font-black uppercase text-indigo-300 block">
                        {wDay.dayName}
                      </span>
                      <span className={`text-lg font-black font-mono ${wDay.isToday ? 'text-lime-400' : 'text-white'}`}>
                        {wDay.dayNumber}
                      </span>
                    </div>

                    <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                      dayGames.length > 0 ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-300'
                    }`}>
                      {dayGames.length}
                    </span>
                  </div>

                  {/* Day Games List */}
                  <div className="space-y-2 flex-1 min-h-[140px]">
                    {dayGames.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center p-2 text-[10px] text-indigo-300/50 font-medium space-y-1">
                        <span>No runs scheduled</span>
                        {onOpenHostGame && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenHostGame(wDay.dateString);
                            }}
                            className="text-lime-400 hover:underline font-bold uppercase text-[9px]"
                          >
                            + Host
                          </button>
                        )}
                      </div>
                    ) : (
                      dayGames.map((game) => (
                        <div
                          key={game.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedGameDetail(game);
                          }}
                          className="p-2.5 bg-indigo-950 border border-white/10 rounded-2xl hover:border-lime-400/60 transition space-y-1.5 shadow-md"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 bg-lime-400/20 text-lime-300 rounded-md">
                              {game.sport}
                            </span>
                            <span className="text-[9px] font-mono text-indigo-300">
                              {game.currentPlayers}/{game.maxPlayers}
                            </span>
                          </div>

                          <h4 className="text-xs font-black italic text-white line-clamp-1">
                            {game.title}
                          </h4>

                          <div className="text-[10px] text-indigo-200/70 flex items-center space-x-1">
                            <MapPin className="w-3 h-3 text-rose-400 shrink-0" />
                            <span className="truncate">{game.courtName}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SELECTED DATE GAME DETAILS & SCHEDULED RUNS PANEL */}
      {selectedDayGamesDate && (
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
            <div>
              <div className="flex items-center space-x-2">
                <Flame className="w-5 h-5 text-rose-400" />
                <h3 className="text-lg font-black italic uppercase tracking-tight text-white">
                  Scheduled Games for {formatReadableDate(selectedDayGamesDate)}
                </h3>
              </div>
              <p className="text-xs text-indigo-200/70 font-semibold">
                {focusedDayGames.length === 0
                  ? 'No pickup games or tournaments scheduled on this date.'
                  : `${focusedDayGames.length} run(s) locked in for this date.`}
              </p>
            </div>

            {onOpenHostGame && (
              <button
                onClick={() => onOpenHostGame(selectedDayGamesDate)}
                className="px-4 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic rounded-xl text-xs uppercase tracking-wider flex items-center space-x-1.5 shadow-md shadow-lime-400/20"
              >
                <Plus className="w-4 h-4 stroke-[3]" />
                <span>Schedule Run on This Date</span>
              </button>
            )}
          </div>

          {focusedDayGames.length === 0 ? (
            <div className="text-center py-8 bg-indigo-950/60 rounded-3xl border border-dashed border-white/10 space-y-3">
              <CalendarIcon className="w-10 h-10 text-indigo-400/50 mx-auto" />
              <p className="text-sm font-semibold text-indigo-200/70">
                The court schedule is clear for this date. Be the first to host a pickup game!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {focusedDayGames.map((game) => (
                <div
                  key={game.id}
                  className="bg-indigo-950/90 border border-white/10 rounded-3xl p-5 hover:border-lime-400/50 transition space-y-3 flex flex-col justify-between shadow-xl"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 bg-lime-400/20 text-lime-300 border border-lime-400/40 rounded-full">
                        {game.sport} • {game.level}
                      </span>

                      <div className="flex items-center space-x-1 text-xs font-mono font-bold text-indigo-200">
                        <Users className="w-3.5 h-3.5 text-lime-400" />
                        <span>{game.currentPlayers}/{game.maxPlayers}</span>
                      </div>
                    </div>

                    <h4 className="text-base font-black italic text-white">
                      {game.title}
                    </h4>

                    <div className="space-y-1 text-xs text-indigo-200/80 font-medium">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-rose-400 shrink-0" />
                        <span className="truncate">{game.courtName}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-indigo-300 shrink-0" />
                        <span>{game.dateTime}</span>
                      </div>
                    </div>

                    {game.description && (
                      <p className="text-xs text-indigo-300/80 line-clamp-2 italic pt-1">
                        "{game.description}"
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="pt-3 border-t border-white/10 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <img
                        src={game.hostAvatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80'}
                        alt={game.hostName}
                        className="w-6 h-6 rounded-full border border-lime-400 object-cover"
                      />
                      <span className="text-xs font-bold text-indigo-200">{game.hostName}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      {onShareGame && (
                        <button
                          onClick={() => onShareGame(game)}
                          className="p-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl transition"
                          title="Share Game"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                      )}

                      {onJoinGame && (
                        <button
                          onClick={() => onJoinGame(game.id)}
                          className="px-3.5 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-xs uppercase rounded-xl transition shadow-md shadow-lime-400/20"
                        >
                          Join Run
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* GAME DETAIL POPUP MODAL */}
      {selectedGameDetail && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-indigo-950 border border-lime-400/40 w-full max-w-lg rounded-[2.5rem] p-6 shadow-2xl space-y-5 text-white relative">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center space-x-2.5">
                <div className="p-2.5 bg-lime-400 text-black rounded-2xl">
                  <CalendarIcon className="w-6 h-6 stroke-[2.5]" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-lime-400 tracking-wider">
                    {selectedGameDetail.sport} • {selectedGameDetail.level}
                  </span>
                  <h3 className="text-xl font-black italic text-white">
                    {selectedGameDetail.title}
                  </h3>
                </div>
              </div>

              <button
                onClick={() => setSelectedGameDetail(null)}
                className="p-2 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl transition"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm text-indigo-200/90 font-medium">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-rose-400" />
                <span>{selectedGameDetail.courtName} ({selectedGameDetail.address})</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-indigo-300" />
                <span>Scheduled: {selectedGameDetail.dateTime}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-lime-400" />
                <span>Players Joined: {selectedGameDetail.currentPlayers} / {selectedGameDetail.maxPlayers}</span>
              </div>

              {selectedGameDetail.description && (
                <div className="p-3 bg-indigo-900/60 rounded-2xl border border-white/10 italic text-xs text-indigo-200">
                  "{selectedGameDetail.description}"
                </div>
              )}
            </div>

            {/* Players list */}
            {selectedGameDetail.playersList && selectedGameDetail.playersList.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-black uppercase text-indigo-300">Roster ({selectedGameDetail.playersList.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedGameDetail.playersList.map((p) => (
                    <div key={p.id} className="flex items-center space-x-1.5 bg-indigo-900/80 px-2.5 py-1 rounded-xl border border-white/10 text-xs font-semibold">
                      <img src={p.avatar} alt={p.name} className="w-4 h-4 rounded-full object-cover" />
                      <span>{p.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2 flex items-center justify-end space-x-3">
              {onShareGame && (
                <button
                  onClick={() => {
                    onShareGame(selectedGameDetail);
                    setSelectedGameDetail(null);
                  }}
                  className="px-4 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl font-bold text-xs uppercase"
                >
                  Share Game
                </button>
              )}

              {onJoinGame && (
                <button
                  onClick={() => {
                    onJoinGame(selectedGameDetail.id);
                    setSelectedGameDetail(null);
                  }}
                  className="px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic text-xs uppercase rounded-xl shadow-lg shadow-lime-400/20"
                >
                  Join Run Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
